import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Skeleton,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  ListItemIcon,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaletteIcon from '@mui/icons-material/Palette';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useNavigate } from 'react-router-dom';
import designStudioService from '../../services/designStudioService';
import designSystemService from '../../services/designSystemService';

const EXAMPLES = [
  { title: 'iOS Onboarding Flow', prompt: 'Design a modern iOS onboarding flow with 3 screens: welcome, features overview, and account setup. Use a clean white design with purple accent color.' },
  { title: 'E-commerce Product Page', prompt: 'Design a mobile product detail page for a premium sneaker. Show product images, size selector, reviews, and an add-to-cart button.' },
  { title: 'Dashboard Analytics', prompt: 'Design a web analytics dashboard with revenue chart, KPI cards, user activity table, and a top navigation bar. Dark mode.' },
  { title: 'Banking App Home', prompt: 'Design an iOS banking app home screen with account balance, recent transactions, quick actions (send, receive, pay), and bottom tab navigation.' },
  { title: 'Signup & Login Screens', prompt: 'Design a signup and login flow with email/password fields, social login buttons (Google, Apple), and a forgot password screen.' },
  { title: 'Food Delivery App', prompt: 'Design a food delivery app with restaurant listings, a restaurant detail page with menu, and a cart/checkout screen.' },
];

// ── New Project Card ───────────────────────────────────────────────────────────

const NewProjectCard = ({ onCreate, designSystems = [] }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('prototype');
  const [fidelity, setFidelity] = useState('high_fidelity');
  const [dsId, setDsId] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      await onCreate(trimmed, type, fidelity, dsId);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{ border: '1px dashed #d0d0d0', borderRadius: 3, background: '#fafafa', height: '100%' }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <Box
            sx={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <AddIcon sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            New Design
          </Typography>
        </Box>

        <TextField
          fullWidth size="small" placeholder="Project name…"
          value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13 } }}
        />

        {/* Type */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block' }}>Type</Typography>
          <ToggleButtonGroup
            value={type} exclusive onChange={(_, v) => v && setType(v)} size="small" sx={{ gap: 0.5 }}
          >
            {[
              { value: 'prototype', label: 'Prototype', icon: <PhoneIphoneIcon sx={{ fontSize: 14 }} /> },
              { value: 'slide_deck', label: 'Slides', icon: <SlideshowIcon sx={{ fontSize: 14 }} /> },
              { value: 'other', label: 'Other' },
            ].map((opt) => (
              <ToggleButton key={opt.value} value={opt.value} sx={{
                fontSize: 11, px: 1.5, py: 0.5, borderRadius: '6px !important',
                gap: 0.5, textTransform: 'none',
                '&.Mui-selected': { background: '#1a1a1a', color: '#fff' },
              }}>
                {opt.icon}{opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Fidelity */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block' }}>Fidelity</Typography>
          <ToggleButtonGroup
            value={fidelity} exclusive onChange={(_, v) => v && setFidelity(v)} size="small"
          >
            {[
              { value: 'high_fidelity', label: 'High fidelity' },
              { value: 'wireframe', label: 'Wireframe' },
            ].map((opt) => (
              <ToggleButton key={opt.value} value={opt.value} sx={{
                fontSize: 11, px: 1.5, py: 0.5, borderRadius: '6px !important',
                textTransform: 'none',
                '&.Mui-selected': { background: '#1a1a1a', color: '#fff' },
              }}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Design system selector */}
        {designSystems.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block' }}>
              Design system (optional)
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={dsId}
                onChange={(e) => setDsId(e.target.value)}
                displayEmpty
                sx={{ fontSize: 12, borderRadius: 1.5 }}
              >
                <MenuItem value=""><em style={{ color: '#aaa', fontSize: 12 }}>None</em></MenuItem>
                {designSystems.map((ds) => (
                  <MenuItem key={ds.id} value={ds.id} sx={{ fontSize: 12 }}>
                    {ds.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Button
          fullWidth variant="contained" onClick={handleCreate}
          disabled={!name.trim() || creating}
          startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
          sx={{
            background: '#1a1a1a', borderRadius: 1.5, textTransform: 'none',
            fontSize: 13, fontWeight: 600,
            '&:hover': { background: '#333' },
          }}
        >
          {creating ? 'Creating…' : 'Create'}
        </Button>
      </CardContent>
    </Card>
  );
};

// ── Project Card ───────────────────────────────────────────────────────────────

const ProjectCard = ({ project, onOpen, onDelete, onDuplicate }) => {
  const [menuAnchor, setMenuAnchor] = React.useState(null);

  const typeIcon = project.projectType === 'slide_deck'
    ? <SlideshowIcon sx={{ fontSize: 14 }} />
    : <PhoneIphoneIcon sx={{ fontSize: 14 }} />;

  const dateStr = project.updatedAt
    ? new Date(project.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '—';

  return (
    <Card elevation={0} sx={{
      border: '1px solid #eee', borderRadius: 3, overflow: 'hidden',
      transition: 'box-shadow 0.15s', '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
    }}>
      <CardActionArea onClick={() => onOpen(project.id)}>
        <Box sx={{
          height: 140,
          background: project.thumbnail
            ? `url(${project.thumbnail}) center/cover`
            : 'linear-gradient(135deg, #f0eee9 0%, #e8e6e1 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {!project.thumbnail && <AutoAwesomeIcon sx={{ fontSize: 32, color: '#c0bdb8', opacity: 0.6 }} />}
        </Box>
      </CardActionArea>
      <CardContent sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{
              fontWeight: 600, fontSize: 13, color: '#1a1a1a',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer',
            }} onClick={() => onOpen(project.id)}>
              {project.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              {typeIcon}
              <Typography variant="caption" sx={{ color: '#999', fontSize: 11 }}>
                {project.projectType?.replace('_', ' ')} · {dateStr}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small"
            onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
            sx={{ color: '#ccc', '&:hover': { color: '#555' } }}>
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        {project.fileCount > 0 && (
          <Chip label={`${project.fileCount} file${project.fileCount !== 1 ? 's' : ''}`}
            size="small" sx={{ mt: 0.75, height: 18, fontSize: 10, background: '#f5f5f5' }} />
        )}
        {project.designSystemId && (
          <Chip icon={<PaletteIcon sx={{ fontSize: 10 }} />} label="has design system"
            size="small" sx={{ mt: 0.5, height: 18, fontSize: 10, background: '#fff7ed', color: '#e85d26' }} />
        )}
      </CardContent>

      {/* Card context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 160 } }}
      >
        <MenuItem onClick={() => { onOpen(project.id); setMenuAnchor(null); }} sx={{ fontSize: 13 }}>
          <ListItemIcon><AutoAwesomeIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          Open
        </MenuItem>
        <MenuItem onClick={() => { onDuplicate(project.id); setMenuAnchor(null); }} sx={{ fontSize: 13 }}>
          <ListItemIcon><ContentCopyIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          Duplicate
        </MenuItem>
        <MenuItem onClick={() => { onDelete(project.id); setMenuAnchor(null); }} sx={{ fontSize: 13, color: '#e53e3e' }}>
          <ListItemIcon><DeleteOutlineIcon sx={{ fontSize: 16, color: '#e53e3e' }} /></ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </Card>
  );
};

// ── Design System CTA card ─────────────────────────────────────────────────────

const DesignSystemCTA = ({ onClick }) => (
  <Card elevation={0} sx={{
    border: '1px solid #eee', borderRadius: 3, background: 'linear-gradient(135deg, #fff7ed, #fff)',
    overflow: 'hidden',
  }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 2,
          background: 'linear-gradient(135deg, #e85d26, #f97316)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <PaletteIcon sx={{ color: '#fff', fontSize: 18 }} />
        </Box>
        <div>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>
            Set up your design system
          </Typography>
          <Typography variant="caption" sx={{ color: '#888', fontSize: 12 }}>
            Brand colors, fonts & tokens
          </Typography>
        </div>
      </Box>
      <Typography variant="body2" sx={{ fontSize: 13, color: '#666', mb: 2, lineHeight: 1.5 }}>
        Tell Claude about your brand — colors, typography, spacing — and every generated design will automatically match your style.
      </Typography>
      <Button
        variant="contained" fullWidth onClick={onClick}
        sx={{
          background: '#e85d26', borderRadius: 2, textTransform: 'none',
          fontWeight: 600, fontSize: 13,
          '&:hover': { background: '#cf4f1e' },
        }}
      >
        Get started
      </Button>
    </CardContent>
  </Card>
);

// ── Main Home ──────────────────────────────────────────────────────────────────

const DesignStudioHome = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [designSystems, setDesignSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0); // 0=Recent, 1=All, 2=Templates, 3=Examples, 4=Design systems
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [proj, ds, tmpl] = await Promise.all([
        designStudioService.listProjects(),
        designSystemService.listDesignSystems(),
        designStudioService.listTemplates().catch(() => []),
      ]);
      setProjects(proj || []);
      setDesignSystems(ds || []);
      setTemplates(tmpl || []);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (name, type, fidelity, dsId) => {
    const result = await designStudioService.createProject(name, type, fidelity, dsId || '');
    navigate(`/design-studio/${result.project.id}`);
  };

  const handleOpen = (id) => navigate(`/design-studio/${id}`);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    await designStudioService.deleteProject(id);
    setProjects((prev) => prev.filter((p) => p.id !== id));
  };

  const handleDuplicate = async (id) => {
    try {
      const result = await designStudioService.duplicateProject(id);
      if (result?.project) {
        setProjects((prev) => [result.project, ...prev]);
      }
    } catch (err) {
      console.error('Duplicate failed:', err);
    }
  };

  const handleExampleClick = async (example) => {
    const result = await designStudioService.createProject(example.title, 'prototype', 'high_fidelity', '');
    navigate(`/design-studio/${result.project.id}?prompt=${encodeURIComponent(example.prompt)}`);
  };

  const filterBySearch = (list) =>
    search.trim()
      ? list.filter((p) => p.name?.toLowerCase().includes(search.toLowerCase()))
      : list;

  const recent = filterBySearch(projects.slice().sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
  const allProjects = filterBySearch(projects);

  const tabProjects = tab === 0 ? recent : tab === 1 ? allProjects : tab === 2 ? filterBySearch(templates) : [];

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', background: '#fff' }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 4, py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
          <div>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 22, color: '#1a1a1a', lineHeight: 1.2 }}>
              Design Studio
            </Typography>
            <Typography variant="body2" sx={{ color: '#888', fontSize: 13 }}>
              AI-powered prototype generation
            </Typography>
          </div>
          <Box sx={{ flex: 1 }} />
          {/* Search */}
          <TextField
            size="small" placeholder="Search projects…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#aaa' }} /></InputAdornment>,
              sx: { borderRadius: 2, fontSize: 13 },
            }}
            sx={{ width: 220 }}
          />
        </Box>

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
          mb: 2, minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, fontSize: 13, textTransform: 'none', px: 2 },
          '& .MuiTabs-indicator': { background: '#1a1a1a' },
        }}>
          <Tab label="Recent" icon={<AccessTimeIcon sx={{ fontSize: 14 }} />} iconPosition="start" />
          <Tab label="All designs" />
          <Tab label="Templates" icon={<BookmarkIcon sx={{ fontSize: 14 }} />} iconPosition="start" />
          <Tab label="Examples" icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />} iconPosition="start" />
          <Tab
            label="Design systems"
            icon={<PaletteIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
            onClick={() => navigate('/design-studio/design-systems')}
          />
        </Tabs>

        <Divider sx={{ mb: 3 }} />

        {/* Examples tab */}
        {tab === 3 && (
          <Grid container spacing={2}>
            {EXAMPLES.map((ex) => (
              <Grid item xs={12} sm={6} md={4} key={ex.title}>
                <Card elevation={0} sx={{
                  border: '1px solid #eee', borderRadius: 3, overflow: 'hidden', cursor: 'pointer',
                  transition: 'box-shadow 0.15s', '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
                }} onClick={() => handleExampleClick(ex)}>
                  <Box sx={{
                    height: 100, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.7)' }} />
                  </Box>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13 }}>{ex.title}</Typography>
                    <Typography variant="caption" sx={{ color: '#888', fontSize: 11, display: 'block', mt: 0.5, lineHeight: 1.4 }}>
                      {ex.prompt.substring(0, 80)}…
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Projects/templates grid */}
        {tab !== 3 && (
          <Grid container spacing={2}>
            {/* New project card — only on projects tabs */}
            {(tab === 0 || tab === 1) && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <NewProjectCard onCreate={handleCreate} designSystems={designSystems} />
              </Grid>
            )}

            {loading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                    <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 3 }} />
                  </Grid>
                ))
              : tabProjects.map((p) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                    <ProjectCard
                      project={p}
                      onOpen={handleOpen}
                      onDelete={handleDelete}
                      onDuplicate={handleDuplicate}
                    />
                  </Grid>
                ))}
          </Grid>
        )}

        {!loading && tab !== 3 && tabProjects.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6, opacity: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#888' }}>
              {tab === 2 ? 'No templates yet — duplicate a project as a template to save it here.'
                : search ? `No results for "${search}"`
                : 'No designs yet — create your first prototype above'}
            </Typography>
          </Box>
        )}

        {/* Design System CTA */}
        {!loading && designSystems.length === 0 && (tab === 0 || tab === 1) && (
          <Box sx={{ mt: 5 }}>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} sm={8} md={5}>
                <DesignSystemCTA
                  onClick={() => navigate('/design-studio/design-systems/new')}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DesignStudioHome;
